import type { User } from '@/types';
import { getActiveScopedRelationships } from '@/lib/auth/scopedRelationshipResolver';

/**
 * Route-derived workspace authority context.
 *
 * The route parameter (/workspaces/:workspaceId/...) is the SOLE source of the
 * workspace scope. This resolver never falls back to a stored selection, the
 * first accessible workspace, a persona default, or any legacy role field. It
 * establishes IDENTITY + MEMBERSHIP context only — it does NOT decide which
 * projects are visible or whether they can be created/managed (those are
 * separate canResolve decisions).
 *
 * This is a CALLER-RUNTIME UX PERMIT mirror. Server RLS/RPC remains the
 * authoritative enforcement boundary.
 */
export type WorkspaceAuthorityStatus = 'resolving' | 'ready' | 'invalid' | 'forbidden';

export interface WorkspaceAuthorityContextValue {
  /** Non-null ONLY when status === 'ready'. Never leaked in forbidden/invalid. */
  workspaceId: string | null;
  organizationId: string | null;
  status: WorkspaceAuthorityStatus;
}

export interface WorkspaceRecord {
  id: string;
  organizationId: string;
  status: 'active' | 'suspended' | 'archived';
}

const FORBIDDEN: WorkspaceAuthorityContextValue = {
  workspaceId: null,
  organizationId: null,
  status: 'forbidden',
};
const INVALID: WorkspaceAuthorityContextValue = {
  workspaceId: null,
  organizationId: null,
  status: 'invalid',
};

/** Conservative identifier format (slug or uuid-ish). Rejects paths/garbage. */
export function isValidWorkspaceIdFormat(id: string | null | undefined): boolean {
  return typeof id === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_-]{1,63}$/.test(id);
}

const ORGANIZATION_ACCOUNT_ROLES = new Set(['organization_owner', 'organization_admin', 'organization_member']);

/**
 * Active membership evidence in a SPECIFIC workspace: an active workspace_member
 * relationship, or an internal organization account whose workspace_id matches
 * (and whose account is active). Never derived from workspace_role/role.
 */
export function hasActiveWorkspaceMembership(
  user: Pick<User, 'account_role' | 'workspace_id' | 'status' | 'scopedRelationships'> | null | undefined,
  workspaceId: string,
): boolean {
  if (!user || user.status === 'suspended') return false;
  if (getActiveScopedRelationships(user, 'workspace', workspaceId).length > 0) return true;
  return (
    user.workspace_id === workspaceId &&
    ORGANIZATION_ACCOUNT_ROLES.has(user.account_role ?? '')
  );
}

/**
 * Pure route-derived resolution:
 *   route id -> format -> workspace lookup -> membership -> status.
 *
 * Fails closed. A forbidden/invalid result exposes NO workspace metadata.
 */
export function resolveWorkspaceAuthority(params: {
  routeWorkspaceId: string | null | undefined;
  user: User | null | undefined;
  lookupWorkspace: (workspaceId: string) => WorkspaceRecord | null;
}): WorkspaceAuthorityContextValue {
  const { routeWorkspaceId, user, lookupWorkspace } = params;

  // Missing / malformed identifier — never select a default workspace.
  if (!routeWorkspaceId || !isValidWorkspaceIdFormat(routeWorkspaceId)) return INVALID;

  const workspace = lookupWorkspace(routeWorkspaceId);
  if (!workspace) return INVALID; // unknown -> not found (no metadata)

  // From here the workspace exists; deny without revealing anything.
  if (!user) return FORBIDDEN;
  if (workspace.status !== 'active') return FORBIDDEN;
  if (!hasActiveWorkspaceMembership(user, workspace.id)) return FORBIDDEN;

  return {
    workspaceId: workspace.id,
    organizationId: workspace.organizationId,
    status: 'ready',
  };
}
